import { apiSlice } from './apiSlice'

/** Mirrors FastAPI `SaleInvoiceOut` (camelCase aliases) — frmCtrlSalesQryInvoice datasheet. */
export interface SaleInvoice {
  id: number
  contractRef: string | null
  buyerName: string | null
  buyersRef: string | null
  contractDate: string | null
  contractWeightKg: number | null
  quality: string | null
  deliveryTerms: string | null
  warehouse: string | null
  paymentTerms: string | null
  paymentWeightTerms: string | null
  contractType: string | null
  toBank: string | null
  invoiceNo: string | null
  vatNo: string | null
  lstDoNo: string | null
  customsInvNo: string | null
  customerInvoiceNumber: string | null
  invoiceType: string | null
  invoiceDate: string | null
  noBag: number | null
  packaging: string | null
  goodsDescription: string | null
  othersDescription: string | null
  finalPriceUsdmt: number | null
  fpFx: number | null
  finalPriceVndkg: number | null
  ppClosingPrice: number | null
  ppDiffs: number | null
  ppPriceUsdmt: number | null
  ppFx: number | null
  ppPriceVndkg: number | null
  stoploss: number | null
  customerInvGw: number | null
  customerInvNw: number | null
  customerInvPrice: string | null
  customerInvTotal: number | null
  customerInvAdjust: number | null
  customerInvAtd: string | null
  customInvGw: number | null
  customInvNw: number | null
  customInvPrice: number | null
  customInvTotal: number | null
  receiptWh: string | null
  receiptGw: number | null
  receiptNw: number | null
  blNumber: string | null
  rcEtaAta: string | null
  docsPresentationDate: string | null
  rcTrackingNo: string | null
  rcDocsDeliveredDate: string | null
  rcDueDate: string | null
  rcPaidDate: string | null
  rcPaidAmount: number | null
  blDate: string | null
  commercialInvDateShow: string | null
  rcPaidDetails: string | null
  released: number | null
  toBeReleased: number | null
  toBePaid: number | null
}

export interface SaleInvoiceListResponse {
  items: SaleInvoice[]
  total: number
  page: number
  pageSize: number
}

export interface SaleInvoiceListQuery {
  page: number
  pageSize: number
  deliveryPeriodFrom?: string
  deliveryPeriodTo?: string
  finalizedFilter?: 'all' | 'yes' | 'no'
}

export interface SuggestIndexResponse {
  index: number
  exists: boolean
}

export interface InvLookupItem { value: string; label: string }
export interface InvLookupResponse { items: InvLookupItem[]; total: number }

/** GET /{id}/form shape (base-table codes) for Edit/Copy hydrate. */
export type SaleInvoiceForm = Record<string, unknown> & { id: number }

/** contract_ref_AfterUpdate derived readonly fields. */
export interface InvContractSnapshot {
  contractFxAuto: number
  contractPriceUnit: string
  contractType: string
  contractPrice: number
  finalPriceUsd: number
  contractGrade: string
  goodsDescription: string
}

/** Create/Update payload — frmCtrlSalesQryInvoiceAdd → invoice_scs (camelCase). */
export interface SaleInvoiceWrite {
  contractRef?: string | null
  toBank?: string | null
  invoiceNo?: string | null
  vatNo?: string | null
  customsInvNo?: string | null
  customerInvoiceNumber?: string | null
  invoiceDate?: string | null
  blDate?: string | null
  commercialInvDate?: string | null
  goodsDescription?: string | null
  othersDescription?: string | null
  fpFx?: number | null
  ppClosingPrice?: number | null
  ppFx?: number | null
  customerInvAdjust?: number | null
  receiptWh?: string | null
  customersCreditNo?: string | null
  reclaim?: string | null
  paidDetails?: string | null
  // Readonly-display fields Access still persists — carried through (hydrated on
  // Edit, empty on New/Copy) so a save does not blank them.
  customerInvGw?: number | null
  customerInvNw?: number | null
  customInvGw?: number | null
  customInvNw?: number | null
  receiptGw?: number | null
  receiptNw?: number | null
  toBeReleased?: number | null
  toBePaid?: number | null
}

export interface SaleInvoiceReceivablesWrite {
  rcEtaAta?: string | null
  rcTrackingNo?: string | null
  rcDocsDeliveredDate?: string | null
  rcDueDate?: string | null
  rcPaidDate?: string | null
  rcPaidAmount?: number | null
  rcPaidDetails?: string | null
}

export interface SaleInvoiceAddDoRequest {
  customsInvNo: string
  invoiceDate?: string | null
  doNo: string
}

const saleInvoiceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSaleInvoices: builder.query<SaleInvoiceListResponse, SaleInvoiceListQuery>({
      query: ({ page, pageSize, deliveryPeriodFrom, deliveryPeriodTo, finalizedFilter }) => ({
        url: '/sale-invoices',
        params: {
          page,
          pageSize,
          ...(deliveryPeriodFrom ? { deliveryPeriodFrom } : {}),
          ...(deliveryPeriodTo ? { deliveryPeriodTo } : {}),
          ...(finalizedFilter ? { finalizedFilter } : {}),
        },
      }),
      providesTags: ['SaleInvoice'],
    }),

    getSaleInvoice: builder.query<SaleInvoice, number>({
      query: (id) => `/sale-invoices/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'SaleInvoice', id }],
    }),

    deleteSaleInvoice: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-invoices/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleInvoice'],
    }),

    /** Email-permissioned hard-delete access (legacy btnDelete rule). */
    getSaleInvoiceCanHardDelete: builder.query<{ canDelete: boolean }, void>({
      query: () => '/sale-invoices/access/can-delete',
    }),

    // ── frmCtrlSalesQryInvoiceAdd suggest/check (Suggest_IndexName invoice_scs) ──
    suggestInvoiceNo: builder.query<SuggestIndexResponse, void>({
      query: () => '/sale-invoices/suggest/invoice-no',
    }),
    checkInvoiceNo: builder.query<SuggestIndexResponse, { index: number; excludeId?: number }>({
      query: ({ index, excludeId }) => ({
        url: '/sale-invoices/check/invoice-no',
        params: { index, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),

    // ── frmCtrlSalesQryInvoiceAdd create / update / form / snapshot ──────────
    getSaleInvoiceForm: builder.query<SaleInvoiceForm, number>({
      query: (id) => `/sale-invoices/${id}/form`,
    }),
    createSaleInvoice: builder.mutation<{ invoice_scs_id: number }, SaleInvoiceWrite>({
      query: (body) => ({ url: '/sale-invoices', method: 'POST', body }),
      invalidatesTags: ['SaleInvoice'],
    }),
    updateSaleInvoice: builder.mutation<{ invoice_scs_id: number }, { id: number; body: SaleInvoiceWrite }>({
      query: ({ id, body }) => ({ url: `/sale-invoices/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleInvoice'],
    }),
    getInvContractSnapshot: builder.query<InvContractSnapshot, string>({
      query: (contractRef) => ({ url: '/sale-invoices/lookups/contract-snapshot', params: { contractRef } }),
      keepUnusedDataFor: 0,
    }),

    // ── Receivables Check + Add DO (shared endpoints for FE-only sub-agents) ──
    updateSaleInvoiceReceivables: builder.mutation<
      { invoice_scs_id: number },
      { id: number; body: SaleInvoiceReceivablesWrite }
    >({
      query: ({ id, body }) => ({ url: `/sale-invoices/${id}/receivables`, method: 'PUT', body }),
      invalidatesTags: ['SaleInvoice'],
    }),
    addSaleInvoiceDo: builder.mutation<
      { invoice_no: string; delivery_order: string },
      { id: number; body: SaleInvoiceAddDoRequest }
    >({
      query: ({ id, body }) => ({ url: `/sale-invoices/${id}/add-do`, method: 'POST', body }),
      invalidatesTags: ['SaleInvoice'],
    }),

    // ── Lookups (combo sources) ──────────────────────────────────────────────
    invLookupContracts: builder.query<InvLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-invoices/lookups/contracts', params: q ? { q } : {} }),
    }),
    invLookupBanks: builder.query<InvLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-invoices/lookups/banks', params: q ? { q } : {} }),
    }),
    invLookupDeliveryOrders: builder.query<InvLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-invoices/lookups/delivery-orders', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useGetSaleInvoicesQuery,
  useGetSaleInvoiceQuery,
  useDeleteSaleInvoiceMutation,
  useGetSaleInvoiceCanHardDeleteQuery,
  useLazySuggestInvoiceNoQuery,
  useLazyCheckInvoiceNoQuery,
  useLazyGetSaleInvoiceFormQuery,
  useCreateSaleInvoiceMutation,
  useUpdateSaleInvoiceMutation,
  useLazyGetInvContractSnapshotQuery,
  useUpdateSaleInvoiceReceivablesMutation,
  useAddSaleInvoiceDoMutation,
  useInvLookupContractsQuery,
  useInvLookupBanksQuery,
  useInvLookupDeliveryOrdersQuery,
} = saleInvoiceApi

export default saleInvoiceApi
